import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { db } from "../firebase";
import MenuNav from "../components/Menunav";
import MobileMenu from "../components/MobileMenu";

export default function PiecesDetacheesPage() {
  const [pieces, setPieces] = useState([]);
  const [filters, setFilters] = useState({
    search: "",
    categorie: "",
    marque: "",
    disponibilite: "",
    etat: "",
    prixMax: "",
    promo: false,
    featured: false,
    origine: false,
    livraisonRapide: false,
    surCommande: false,
  });
  const [currentPage, setCurrentPage] = useState(1);

  const itemsPerPage = 12;

  useEffect(() => {
    const fetchPieces = async () => {
      try {
        const q = query(
          collection(db, "pieces_detachees"),
          where("enLigne", "==", true)
        );
        const snap = await getDocs(q);
        setPieces(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (error) {
        console.error(error);
      }
    };

    fetchPieces();
  }, []);

  const categories = useMemo(
    () => [...new Set(pieces.map((p) => p.categorie).filter(Boolean))],
    [pieces]
  );

  const marques = useMemo(
    () => [...new Set(pieces.map((p) => p.marque).filter(Boolean))],
    [pieces]
  );

  const filteredPieces = useMemo(() => {
    return pieces.filter((p) => {
      const text =
        `${p.nom || ""} ${p.categorie || ""} ${p.marque || ""} ${p.reference || ""}`.toLowerCase();

      return (
        (!filters.search || text.includes(filters.search.toLowerCase())) &&
        (!filters.categorie || p.categorie === filters.categorie) &&
        (!filters.marque || p.marque === filters.marque) &&
        (!filters.disponibilite || p.disponibilite === filters.disponibilite) &&
        (!filters.etat || p.etat === filters.etat) &&
        (!filters.prixMax || Number(p.prix || 0) <= Number(filters.prixMax)) &&
        (!filters.promo || p.isPromo) &&
        (!filters.featured || p.isFeatured) &&
        (!filters.origine || p.origine) &&
        (!filters.livraisonRapide || p.livraisonRapide) &&
        (!filters.surCommande || p.surCommande)
      );
    });
  }, [pieces, filters]);

  const totalPages = Math.ceil(filteredPieces.length / itemsPerPage);

  const paginatedPieces = useMemo(() => {
    return filteredPieces.slice(
      (currentPage - 1) * itemsPerPage,
      currentPage * itemsPerPage
    );
  }, [filteredPieces, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const resetFilters = () => {
    setFilters({
      search: "",
      categorie: "",
      marque: "",
      disponibilite: "",
      etat: "",
      prixMax: "",
      promo: false,
      featured: false,
      origine: false,
      livraisonRapide: false,
      surCommande: false,
    });
  };

  return (
    <>
      <Helmet>
        <title>Pièces détachées auto | THE BRIDGE</title>
        <meta
          name="description"
          content="Découvrez nos pièces détachées auto disponibles chez THE BRIDGE : carrosserie, moteur, accessoires, éclairage et plus encore."
        />
      </Helmet>

      <MenuNav />
      <MobileMenu />

      <div className="container py-5">
        <div className="text-center mb-4">
          <span className="badge bg-danger px-3 py-2 mb-3">
            Pièces détachées
          </span>
          <h1 className="fw-bold">Trouvez la bonne pièce pour votre véhicule</h1>
          <p className="text-muted">
            Commandez vos pièces auprès de THE BRIDGE en toute simplicité.
          </p>
        </div>

        <div className="row g-4">
          {/* Filtres */}
          <div className="col-lg-3">
            <div className="card shadow-sm border-0 sticky-top" style={{ top: "100px" }}>
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="fw-bold mb-0">Filtres</h5>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={resetFilters}
                  >
                    Réinitialiser
                  </button>
                </div>

                <div className="mb-3">
                  <label className="form-label">Recherche</label>
                  <input
                    className="form-control"
                    name="search"
                    placeholder="Nom, marque, référence..."
                    value={filters.search}
                    onChange={handleChange}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Catégorie</label>
                  <select
                    className="form-select"
                    name="categorie"
                    value={filters.categorie}
                    onChange={handleChange}
                  >
                    <option value="">Toutes les catégories</option>
                    {categories.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label">Marque</label>
                  <select
                    className="form-select"
                    name="marque"
                    value={filters.marque}
                    onChange={handleChange}
                  >
                    <option value="">Toutes les marques</option>
                    {marques.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label">Disponibilité</label>
                  <select
                    className="form-select"
                    name="disponibilite"
                    value={filters.disponibilite}
                    onChange={handleChange}
                  >
                    <option value="">Toutes</option>
                    <option value="en stock">En stock</option>
                    <option value="sur commande">Sur commande</option>
                    <option value="rupture">Rupture</option>
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label">État</label>
                  <select
                    className="form-select"
                    name="etat"
                    value={filters.etat}
                    onChange={handleChange}
                  >
                    <option value="">Tous</option>
                    <option value="neuf">Neuf</option>
                    <option value="occasion">Occasion</option>
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label">Prix max</label>
                  <input
                    type="number"
                    className="form-control"
                    name="prixMax"
                    value={filters.prixMax}
                    onChange={handleChange}
                    placeholder="Ex: 150000"
                  />
                </div>

                <div className="d-flex flex-column gap-2">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="promo"
                      name="promo"
                      checked={filters.promo}
                      onChange={handleChange}
                    />
                    <label className="form-check-label" htmlFor="promo">
                      Promotions
                    </label>
                  </div>

                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="featured"
                      name="featured"
                      checked={filters.featured}
                      onChange={handleChange}
                    />
                    <label className="form-check-label" htmlFor="featured">
                      En vedette
                    </label>
                  </div>

                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="origine"
                      name="origine"
                      checked={filters.origine}
                      onChange={handleChange}
                    />
                    <label className="form-check-label" htmlFor="origine">
                      Pièce d'origine
                    </label>
                  </div>

                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="livraisonRapide"
                      name="livraisonRapide"
                      checked={filters.livraisonRapide}
                      onChange={handleChange}
                    />
                    <label className="form-check-label" htmlFor="livraisonRapide">
                      Livraison rapide
                    </label>
                  </div>

                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="surCommande"
                      name="surCommande"
                      checked={filters.surCommande}
                      onChange={handleChange}
                    />
                    <label className="form-check-label" htmlFor="surCommande">
                      Sur commande
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contenu */}
          <div className="col-lg-9">
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
              <h5 className="fw-bold mb-0">
                {filteredPieces.length} pièce(s) trouvée(s)
              </h5>
            </div>

            <div className="row g-4">
              {paginatedPieces.map((piece) => (
                <div key={piece.id} className="col-6 col-md-4 col-xl-3">
                  <div className="card h-100 shadow-sm border-0 piece-card">
                    <img
                      src={piece.imageUrls?.[0] || "/assets/default.jpg"}
                      alt={piece.nom}
                      className="card-img-top"
                      style={{ height: "200px", objectFit: "cover" }}
                    />

                    <div className="card-body d-flex flex-column">
                      <div className="d-flex flex-wrap gap-2 mb-2">
                        {piece.isPromo && (
                          <span className="badge bg-danger">Promo</span>
                        )}
                        {piece.isPopular && (
                          <span className="badge bg-warning text-dark">Populaire</span>
                        )}
                        {piece.isFeatured && (
                          <span className="badge bg-primary">Vedette</span>
                        )}
                        {piece.livraisonRapide && (
                          <span className="badge bg-success">Livraison rapide</span>
                        )}
                        {piece.origine && (
                          <span className="badge bg-dark">Origine</span>
                        )}
                        {piece.surCommande && (
                          <span className="badge bg-info text-dark">Sur commande</span>
                        )}
                      </div>

                      <h6 className="fw-bold">{piece.nom}</h6>

                      <p className="text-muted small mb-1">
                        {piece.categorie} • {piece.marque}
                      </p>

                      <p className="text-muted small mb-2">
                        {piece.reference || "Référence non précisée"}
                      </p>

                      <p className="fw-bold text-primary mb-3">
                        {piece.prix > 0
                          ? `${Number(piece.prix).toLocaleString("fr-FR")} FCFA`
                          : "Prix sur demande"}
                      </p>

                      <div className="mt-auto">
                        <Link
                          to={`/piece/${piece.slug}`}
                          className="btn btn-outline-primary btn-sm w-100"
                        >
                          Voir détails
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {filteredPieces.length === 0 && (
                <div className="col-12">
                  <div className="alert alert-light border text-center">
                    Aucune pièce trouvée.
                  </div>
                </div>
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="d-flex justify-content-center mt-5">
                <ul className="pagination flex-wrap">
                  {Array.from({ length: totalPages }, (_, i) => (
                    <li
                      key={i}
                      className={`page-item ${currentPage === i + 1 ? "active" : ""}`}
                    >
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(i + 1)}
                      >
                        {i + 1}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <section className="mt-5">
              <div className="p-4 rounded-4 border bg-white shadow-sm text-center">
                <h4 className="fw-bold mb-2">Besoin d’un entretien ou d’une maintenance ?</h4>
                <p className="text-muted mb-3">
                  Prenez rendez-vous avec THE BRIDGE pour l’entretien, le diagnostic ou la réparation de votre véhicule.
                </p>
            
                <div className="d-flex flex-column flex-md-row justify-content-center gap-3">
                  <Link to="/maintenance-rendezvous" className="btn btn-primary">
                    Prendre rendez-vous
                  </Link>
            
                  <a
                    href="https://wa.me/22879309400"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-outline-success"
                  >
                    Contacter sur WhatsApp
                  </a>
                </div>
              </div>
            </section>
            {/* Pièce non trouvée */}
            <section className="mt-5">
              <div className="p-4 rounded-4 border bg-light shadow-sm text-center">
                <h4 className="fw-bold mb-2">Vous ne trouvez pas votre pièce ?</h4>
                <p className="text-muted mb-3">
                  Envoyez-nous votre demande et THE BRIDGE vous aidera à trouver la bonne pièce.
                </p>

                <div className="d-flex flex-column flex-md-row justify-content-center gap-3">
                  <a
                    href="https://wa.me/22879309400?text=Bonjour%2C%20je%20cherche%20une%20pi%C3%A8ce%20d%C3%A9tach%C3%A9e%20sp%C3%A9cifique."
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-success"
                  >
                    Demander sur WhatsApp
                  </a>

                  <Link to="/contact" className="btn btn-outline-primary">
                    Faire une demande
                  </Link>
                </div>
              </div>
            </section>
          </div>
        </div>

        <style>{`
          .piece-card {
            transition: transform 0.3s ease, box-shadow 0.3s ease;
          }

          .piece-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 1rem 2rem rgba(0,0,0,.12) !important;
          }
        `}</style>
      </div>
    </>
  );
}
